import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Plus } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";

import { apiDeletePhysiciansUuid } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { useCountries } from "../../../catalogs/countries";
import { Button } from "../../../components/ui-kit/button";
import { Checkbox } from "../../../components/ui-kit/fields/checkbox";
import { CellEnum } from "../../../components/ui-kit/tables/cells/cell-enum";
import { CellTooltipList } from "../../../components/ui-kit/tables/cells/cell-tooltip-list";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import {
  getCountryOptions,
  getRegionOptions,
  regionDisabled,
  regionPredicate,
  uniqueArrayValueOptions,
} from "../../../utils/filter-presets";
import { assert, requireNonNull } from "../../../utils/utils";
import {
  PatientStatusCell,
  PatientStatusIndex,
  PatientStatusTotalsRow,
} from "../common/patient-status-breakdown";
import { AddPhysicianDialog } from "./add-physician-dialog";
import { PhysicianEditDialog } from "./physician-edit-dialog";
import { PhysiciansItem } from "./physicians";

export const PhysiciansRoute = observer(function PhysiciansRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const admin = props.app.requireAdminShit();
  const model = admin.physicians;
  assert(model, "Physicians model not initialized");
  const countries = useCountries();

  const [showAddDialog, setShowAddDialog] = useState(false);

  const statusIndex = useMemo(() => {
    const index = new PatientStatusIndex();
    void index.load();
    return index;
  }, []);

  return (
    <>
      {showAddDialog && (
        <AddPhysicianDialog
          admin={admin}
          onClose={() => setShowAddDialog(false)}
          onSaved={() => {
            void admin.refreshPhysicians();
          }}
        />
      )}

      <DataTable
        persistState
        editMode="dialog"
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "physician", other: "physicians" })}`
        }
        filterActions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => setShowAddDialog(true)}
          >
            <Trans>New physician</Trans>
          </Button>
        }
        columns={[
          {
            header: <Trans>Name</Trans>,
            attr: "full_name",
            type: "string",
            sort: true,
            initSort: "asc",
            filter: {
              kind: "text",
              group: t`Identity`,
              placeholder: t`Search…`,
            },
            className: "font-bold",
            align: "left",
            width: 300,
          },
          {
            header: <Trans>Title</Trans>,
            attr: "title_before",
            type: "string",
            width: 60,
          },
          {
            header: <Trans>Country</Trans>,
            attr: "country",
            type: "enum",
            values: countries.countriesEnum,
            filter: {
              kind: "multi",
              group: t`Location`,
              options: getCountryOptions,
            },
          },
          {
            header: <Trans>Region</Trans>,
            attr: "region",
            filter: {
              kind: "multi",
              group: t`Location`,
              options: (table) => getRegionOptions(table, countries),
              disabled: regionDisabled,
              disabledMessage: t`Select a country first`,
              predicate: regionPredicate,
            },
            cell: (props) => (
              <CellEnum
                {...props}
                attr={requireNonNull(props.attr)}
                values={countries.getRegionsEnum(props.item.country)}
              />
            ),
          },
          {
            header: <Trans>Bypass geography checks</Trans>,
            attr: "bypass_geography_checks",
            cell: (props) => (
              <div className="flex gap-2">
                <Checkbox
                  label={t`Country`}
                  disabled
                  value={props.item.bypass_geography_checks?.[0] ?? false}
                />
                /
                <Checkbox
                  label={t`Region`}
                  disabled
                  value={props.item.bypass_geography_checks?.[1] ?? false}
                />
              </div>
            ),
          },
          {
            header: <Trans>Email</Trans>,
            attr: "email",
            type: "string",
            copyable: true,
          },
          {
            header: <Trans>Phone</Trans>,
            attr: "phone",
            type: "array",
            copyable: true,
          },
          {
            header: <Trans>User</Trans>,
            attr: "username",
          },
          {
            header: <Trans>Monitoring user(s)</Trans>,
            attr: "monitoringUserNames",
            type: "array",
            width: 160,
            filter: {
              kind: "multi",
              group: t`Monitoring`,
              options: uniqueArrayValueOptions<PhysiciansItem>(
                "monitoringUserNames",
              ),
            },
            cell: ({ item }) => (
              <CellTooltipList items={item.monitoringUserNames} />
            ),
          },
          {
            header: <Trans># of patients</Trans>,
            attr: "numPatients",
            type: "number",
            sort: true,
            width: 220,
            align: "left",
            cell: ({ item }) => (
              <PatientStatusCell patients={item.patients} index={statusIndex} />
            ),
          },
          {
            header: <Trans># of clinics</Trans>,
            attr: "numClinics",
            width: 30,
          },
        ]}
        model={model}
        renderEditDialog={({ item, onClose, onSave }) => (
          <PhysicianEditDialog
            item={item}
            admin={admin}
            onClose={onClose}
            onSave={onSave}
          />
        )}
        // renderEditDialog's onSave routes its patch through onEdit
        onEdit={(editedField, item) =>
          admin.updatePhysician(item.uuid, editedField)
        }
        onDelete={async (item) => {
          await apiDeletePhysiciansUuid({ path: { uuid: item.uuid } });
          await admin.refreshPhysicians();
        }}
        rowsPerPage={10}
        foot={(table) => (
          <PatientStatusTotalsRow
            rows={table.getFilteredRowModel().rows}
            colSpan={table.getVisibleLeafColumns().length}
            index={statusIndex}
          />
        )}
      />
    </>
  );
});
